// announcements-layer/services/announcements.service.ts
import { BaseService } from '@common/http-layer/service/base.service'

import type { AnnouncementDetail, AnnouncementListParams, AnnouncementListResponse, UnreadCountResponse } from '../types/announcement'

/** Mark-as-read requests are chunked so a large backlog never overruns the API. */
const MARK_READ_CHUNK_SIZE = 100

export class AnnouncementsService extends BaseService {
  constructor() {
    super({
      baseURL: 'REST_API_URLS',
      basePath: '',
      headers: {
        version: '2021-07-28',
      },
    })
  }

  private endpoint(path: string, locationId: string): string {
    if (!locationId) throw new Error('Location ID is required')
    return `/courses/learners/locations/${locationId}/announcements${path}`
  }

  async getAnnouncements(locationId: string, params: AnnouncementListParams = {}): Promise<AnnouncementListResponse> {
    const query: Record<string, string | number | boolean> = {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    }
    if (params.search) query.search = params.search
    if (params.productIds?.length) query.productIds = params.productIds.join(',')
    if (params.includeAllCourses) query.includeAllCourses = true
    if (params.publishedAfter) query.publishedAfter = params.publishedAfter
    if (params.publishedBefore) query.publishedBefore = params.publishedBefore

    const { data } = await this.get<AnnouncementListResponse>(this.endpoint('', locationId), {
      params: query,
      locationId,
    })
    return {
      page: data?.page ?? 1,
      limit: data?.limit ?? 20,
      total: data?.total ?? 0,
      items: data?.items ?? [],
    }
  }

  async getUnreadCount(locationId: string): Promise<number> {
    const { data } = await this.get<UnreadCountResponse>(this.endpoint('/unread-count', locationId), {
      locationId,
    })
    return data?.count ?? 0
  }

  async getAnnouncementById(locationId: string, id: string): Promise<AnnouncementDetail> {
    const { data } = await this.get<AnnouncementDetail>(this.endpoint(`/${id}`, locationId), {
      locationId,
    })
    return data
  }

  async markManyAsRead(locationId: string, announcementIds: string[]): Promise<void> {
    if (!announcementIds.length) return

    const chunks: string[][] = []
    for (let index = 0; index < announcementIds.length; index += MARK_READ_CHUNK_SIZE) {
      chunks.push(announcementIds.slice(index, index + MARK_READ_CHUNK_SIZE))
    }

    await Promise.all(chunks.map(chunk => this.post(this.endpoint('/read', locationId), { announcementIds: chunk }, { locationId })))
  }
}
